import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PRICING } from '@/constants/testIds';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

const monthlyFeatures = [
  'Unlimited saved queries',
  '200+ live OSINT streams',
  'Entity graph exports',
  'Priority signal routing',
  'Audit-grade activity log',
];

const yearlyFeatures = [
  'Everything in monthly',
  '2 months free ($228 → $190)',
  'Yearly usage report',
  'Early access to new sources',
  'Dedicated onboarding channel',
];

const PlanCard = ({ plan, price, cadence, features, badge, onPick, busy, testId }) => (
  <div className="c-card c-card--hover flex flex-col">
    <div className="flex items-start justify-between mb-4">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">PLAN</div>
        <h2 className="text-xl uppercase tracking-tight font-bold mt-1">{plan}</h2>
      </div>
      {badge && <span className="c-badge c-badge--active">{badge}</span>}
    </div>
    <div className="mb-6 flex items-baseline gap-2">
      <span className="text-4xl font-bold text-white">${price}</span>
      <span className="text-xs text-gray-500 tracking-widest uppercase">/ {cadence}</span>
    </div>
    <ul className="space-y-2 mb-6 flex-1">
      {features.map((f) => (
        <li key={f} className="text-xs text-gray-400 flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-[#38BDF8] mt-0.5 shrink-0" strokeWidth={3} />
          <span>{f}</span>
        </li>
      ))}
    </ul>
    <button
      data-testid={testId}
      onClick={onPick}
      disabled={busy}
      className="c-btn-primary w-full mt-auto"
    >
      {busy ? 'ROUTING…' : 'UPGRADE_CLEARANCE'}
    </button>
  </div>
);

export const PricingPage = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState('');

  const pick = async (plan) => {
    if (!user) {
      nav('/login', { state: { from: '/pricing' } });
      return;
    }
    setBusy(plan);
    try {
      const response = await api.post('/checkout', {
        plan,
        origin_url: window.location.origin,
      });

      // We kijken nu naar zowel data.url als naar de hele response
      const redirectUrl = response.data?.url || response.data?.checkout_url || response.url;
      
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        // Als we hier komen, is de backend succesvol, maar is de URL leeg
        console.error("Backend succesvol, maar geen URL ontvangen:", response);
        toast.error("Betaalsessie aangemaakt, maar kon niet doorverwijzen.");
        setBusy('');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Checkout failed');
      setBusy('');
    }
  };

  return (
    <div data-testid={PRICING.container} className="max-w-4xl mx-auto px-6 md:px-8 pt-16 pb-24">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#38BDF8] mb-3">
        // ACCESS_TIERS
      </div>
      <h1 className="text-3xl sm:text-4xl font-bold uppercase tracking-tighter mb-4">
        Upgrade Clearance<span className="caelix-caret"></span>
      </h1>
      <p className="text-sm text-gray-400 mb-10 max-w-xl">
        One product, two cadences. Cancel anytime from the Pro control room. Both
        plans include the full Caelix Intelligence surface — pricing is about billing rhythm.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <PlanCard
          plan="Pro · Monthly"
          price="19"
          cadence="month"
          features={monthlyFeatures}
          onPick={() => pick('monthly')}
          busy={busy === 'monthly'}
          testId={PRICING.monthlyBtn}
        />
        <PlanCard
          plan="Pro · Yearly"
          price="190"
          cadence="year"
          features={yearlyFeatures}
          badge="2 MONTHS FREE"
          onPick={() => pick('yearly')}
          busy={busy === 'yearly'}
          testId={PRICING.yearlyBtn}
        />
      </div>
      <div className="mt-10 text-[10px] text-gray-600 tracking-widest uppercase">
        &gt; billing handled by stripe · cards + wallets accepted · fully refundable in 14 days
      </div>
    </div>
  );
};

export default PricingPage;
